import { useEffect, useState } from 'react';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { P2GFade, P2GGrid } from '@parcel2go/core-ui';
import { CustomerSetting } from '~/lib/api/me/types';
import Setting from './setting';
import useLocalStorage from '~/hooks/use-local-storage';
import useUserDismissedFeatures from './use-user-dismissed-features';

type props = {
  area: 'BOOK' | 'ACCOUNTS';
  features: { feature: string; timeToShow?: number; userCanDismiss: boolean; expanded?: boolean }[];
  wrapper: (x: React.ReactElement) => React.ReactElement;
};

const MyAccountFeaturePropmpts: { [key: string]: number } = {};

const InstaSetting = ({ area, wrapper, features }: props) => {
  const { getUserSettings, upsertUserSetting } = useMeApi();
  const [settings, setSettings] = useState<CustomerSetting[]>();
  const { setValue, storedValue } = useLocalStorage(`MYACCOUNT-FEATURE-PROMPTS-${area}`, MyAccountFeaturePropmpts);
  const { userDismissedFeatures, hideFeature } = useUserDismissedFeatures(area);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async (show: string[]) => {
      const res = await getUserSettings(show.length == 1 ? show[0] : undefined);
      if (res.success && res.result) {
        // Limit the features to those that can be shown
        const apiFeatures = res.result.settings.filter((s) => show.includes(s.name));

        // Update the local storage for the features that are shown
        const newStoredValue = { ...storedValue };
        apiFeatures.forEach((f) => {
          newStoredValue[f.name] = (newStoredValue[f.name] || 0) + 1;
        });
        setValue(newStoredValue);

        // Return only the features that can be shown
        setSettings(apiFeatures);
      }
    };

    // Only show features that have not been shown enough times
    const featureDisplayLimits: string[] = [];
    features
      .filter((f) => !f.userCanDismiss || !userDismissedFeatures.includes(f.feature))
      .forEach((f) => {
        if (!f.timeToShow) {
          featureDisplayLimits.push(f.feature);
        } else {
          if (!storedValue[f.feature] || storedValue[f.feature] < f.timeToShow) {
            featureDisplayLimits.push(f.feature);
          }
        }
      });

    if (featureDisplayLimits.length) {
      load(featureDisplayLimits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if there are no settings to show or all settings have been dismissed
  if (
    !settings ||
    settings.length === 0 ||
    settings.every(
      (s) => features.find((x) => x.feature === s.name)?.userCanDismiss && userDismissedFeatures.includes(s.name)
    )
  ) {
    return null;
  }

  return wrapper(
    <P2GGrid container spacing={4} direction="column" textAlign={'left'}>
      {settings?.map((s) => {
        const canDismiss = features.find((x) => x.feature === s.name)?.userCanDismiss ? true : false;
        const hideDetails = features.find((x) => x.feature === s.name)?.expanded ? false : true;
        return (
          <Setting
            onUserDismiss={canDismiss ? hideFeature : undefined}
            hideDetails={hideDetails}
            onSave={async (n, v) => {
              setSaving(true);
              setSettings((prev) => prev?.map((setting) => (setting.name === n ? { ...setting, value: v } : setting)));
              await upsertUserSetting(n, v);
              setSaving(false);
            }}
            key={s.name}
            saving={saving}
            wrapper={(x) => (
              <P2GGrid item>
                <P2GFade in>
                  <>{x}</>
                </P2GFade>
              </P2GGrid>
            )}
            name={s.name}
            value={s.value}
          />
        );
      })}
    </P2GGrid>
  );
};

export default InstaSetting;
